import { Theme } from '@mui/material_v7';
import { formLabelClasses } from '@mui/material_v7/FormLabel';
import { alpha, Components } from '@mui/material_v7/styles';

import flavours from './flavours';

const applicationName = import.meta.env.VITE_APPLICATION_NAME as keyof typeof flavours;
const applicationBasedStyles = flavours[applicationName];

const getOverriddenStyles = (theme: Theme): Components => ({
  MuiTypography: {
    defaultProps: {
      color: theme.palette.grey.black,
    },
    variants: [
      {
        props: { color: 'errorContrastTextLight' },
        style: {
          color: theme.palette.error[600],
        },
      },
    ],
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: theme.palette.grey[200],
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        '.MuiInputBase-root': {
          boxSizing: 'border-box',
          height: '40px',
          fontSize: '14px',
          lineHeight: '20px',
          backgroundColor: theme.palette.secondary[400],

          '&:hover': {
            fieldset: {
              borderColor: theme.palette.primary[400],
            },
          },

          '&.Mui-focused': {
            color: theme.palette.grey.black,
            backgroundColor: 'transparent',

            fieldset: {
              border: `1px solid ${theme.palette.primary[400]}`,
            },
          },

          '&.Mui-error': {
            fieldset: {
              borderColor: theme.palette.error[500],
            },
          },

          '&.Mui-disabled': {
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.grey[600],

            fieldset: {
              border: 'none',
            },
          },

          '&.MuiInputBase-adornedEnd': {
            paddingRight: '10px',
          },

          fieldset: {
            top: 0,
            borderColor: theme.palette.grey[200],

            legend: {
              display: 'none',
            },
          },
          '.MuiInputAdornment-root': {
            color: theme.palette.grey[600],
          },
        },

        '.MuiFormHelperText-root': {
          marginLeft: 0,
          marginRight: 0,
          color: theme.palette.grey[600],

          '&.Mui-disabled': {
            color: theme.palette.grey[600],
          },
          '&.Mui-error': {
            color: theme.palette.error[500],
          },
        },
      },
    },
  },

  MuiInput: {
    styleOverrides: {
      input: {
        '::placeholder': {
          opacity: 1,
          color: theme.palette.grey[600],
        },
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        position: 'relative',
        marginBottom: theme.spacing(1),
        transform: 'none',
        color: theme.palette.grey[800],
        ...theme.typography.body2,

        '&.Mui-focused': {
          color: theme.palette.grey[800],
        },

        '&.Mui-error': {
          color: theme.palette.grey[800],
        },

        '&.Mui-disabled': {
          color: theme.palette.grey[800],
        },

        '.MuiFormLabel-asterisk': {
          color: theme.palette.error[500],
        },
      },
    },
  },

  MuiButton: {
    // TODO: make size="large" - by default
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        boxShadow: 'none',
        // 8px for TextButton and 16 for other buttons
        paddingLeft: '8px',
        paddingRight: '8px',

        '&.MuiButton-sizeMedium': {
          height: '32px',
          fontSize: '12px',
        },

        '&.MuiButton-sizeLarge': {
          height: '40px',
          fontSize: '14px',
        },

        '&:hover': {
          boxShadow: 'none',
        },
      },
      containedPrimary: {
        paddingLeft: '16px',
        paddingRight: '16px',
        backgroundColor: theme.palette.primary[500],

        '&:hover': {
          backgroundColor: theme.palette.primary[600],
        },

        '&.Mui-disabled': {
          backgroundColor: theme.palette.primary[500],
          opacity: 0.3,
          color: theme.palette.grey.white,
        },
      },
      outlinedPrimary: {
        paddingLeft: '16px',
        paddingRight: '16px',
        color: theme.palette.primary[500],
        borderColor: theme.palette.primary[500],

        '&:hover': {
          backgroundColor: theme.palette.primary[200],
          color: theme.palette.primary[600],
          borderColor: theme.palette.primary[600],
        },

        '&.Mui-disabled': {
          color: theme.palette.primary[300],
          borderColor: theme.palette.primary[300],
        },
      },
      containedSecondary: {
        paddingLeft: '16px',
        paddingRight: '16px',
        backgroundColor: theme.palette.secondary[500],
        color: theme.palette.secondary[700],

        '&:hover': {
          backgroundColor: theme.palette.secondary[600],
        },

        '&.Mui-disabled': {
          backgroundColor: theme.palette.secondary[500],
          opacity: 0.3,
          color: theme.palette.secondary[700],
        },
      },
      containedSuccess: {
        paddingLeft: '16px',
        paddingRight: '16px',
        backgroundColor: theme.palette.success[500],
        color: theme.palette.grey.white,

        '&:hover': {
          backgroundColor: theme.palette.success[600],
        },

        '&.Mui-disabled': {
          backgroundColor: theme.palette.success[500],
          opacity: 0.3,
          color: theme.palette.grey.white,
        },
      },
      containedError: {
        paddingLeft: '16px',
        paddingRight: '16px',
        backgroundColor: theme.palette.error[500],

        '&:hover': {
          backgroundColor: theme.palette.error[600],
        },

        '&.Mui-disabled': {
          backgroundColor: theme.palette.error[500],
          opacity: 0.3,
          color: theme.palette.grey.white,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          color: theme.palette.grey[400],
        },

        '&:hover': {
          backgroundColor: theme.palette.primary[200],
          color: theme.palette.primary[600],
        },

        '&.square': {
          borderRadius: '4px',
          '.MuiTouchRipple-child': { borderRadius: '6px' },
        },
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        color: theme.palette.grey[600],

        '&:hover': {
          backgroundColor: theme.palette.primary[200],
        },

        '&.Mui-checked': {
          color: theme.palette.primary[500],
        },

        '&.Mui-disabled': {
          color: theme.palette.grey[500],
        },

        '&.MuiRadio-sizeSmall': {
          '.MuiSvgIcon-root': {
            fontSize: 19.2, // 16x16 svg size
          },
        },
      },
    },
  },
  // used for MuiRadio labels
  MuiFormControlLabel: {
    styleOverrides: {
      label: {
        color: theme.palette.grey.black,
        fontSize: '14px',
        lineHeight: '20px',

        '&.Mui-disabled': {
          color: theme.palette.grey[800],
        },
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: theme.palette.grey[600],
        '&:hover': {
          backgroundColor: 'rgba(52, 52, 52, 0.08)',
        },
        '&.Mui-checked': {
          color: theme.palette.primary[500],
        },
      },
    },
  },

  MuiSvgIcon: {
    variants: [
      {
        props: { fontSize: 'small' },
        style: {
          fontSize: '16px',
        },
      },
      {
        props: { fontSize: 'medium' },
        style: {
          fontSize: '20px',
        },
      },
      {
        props: { fontSize: 'large' },
        style: {
          fontSize: '24px',
        },
      },
    ],
  },

  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '.MuiInputBase-root': {
          input: {
            '::placeholder': {
              opacity: 1,
              color: theme.palette.grey[600],
            },
          },
        },
      },
      tag: {
        fontSize: '12px',
        height: '24px',
        lineHeight: '16px',
        borderRadius: '12px',
        maxWidth: 'calc(100% - 20px)', // for multiselect +1 placeholder

        '.MuiSvgIcon-root': {
          color: theme.palette.primary[400],

          '&:hover': {
            color: theme.palette.primary[500],
          },
        },

        '&+span': {
          display: 'flex',
          alignItems: 'center',
        },
      },
      paper: {
        boxShadow: '0px 0.5px 2px 0px rgba(0, 0, 0, 0.04), 0px 1px 4px 0px rgba(0, 0, 0, 0.20)',
      },
      listbox: {
        '.MuiCheckbox-root': {
          paddingLeft: 0,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      option: {
        paddingTop: '10px',
        paddingBottom: '10px',
        fontSize: '14px',
        lineHeight: '20px',

        '&:hover': {
          backgroundColor: `${theme.palette.primary[200]}`,
        },
        '&.Mui-focused:hover': {
          backgroundColor: theme.palette.primary[200],
        },
        '&.Mui-focused:not(:hover)': {
          backgroundColor: theme.palette.primary[200],
        },
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      select: {
        padding: '10px 16px',
        background: theme.palette.secondary[400],
        ...theme.typography.body2,
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: theme.palette.primary[200],
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary[200],
        },
        '&.Mui-selected:hover': {
          backgroundColor: theme.palette.primary[300],
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        borderColor: theme.palette.grey[200],
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
  },

  MuiTablePaginationActions: {
    styleOverrides: {
      root: {
        '.MuiIconButton-root:not(.Mui-disabled)': {
          color: theme.palette.grey[600],
        },
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        '&.MuiAlert-colorError': {
          backgroundColor: theme.palette.error[200],
          color: theme.palette.error[500],
        },

        '&.MuiAlert-colorWarning': {
          backgroundColor: theme.palette.warning[300],
          color: theme.palette.warning[600],
        },
      },
      icon: {
        display: 'flex',
        alignItems: 'center',
      },
      action: {
        marginRight: 0,
        paddingTop: 0,
        display: 'flex',
        alignItems: 'center',

        button: {
          fontWeight: 700,
        },
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        '&.MuiBadgeChip': {
          '.MuiChip-label': {
            paddingLeft: theme.spacing(1.25),
            paddingRight: theme.spacing(1.25),
          },
        },
        '&.MuiSidebarChip': {
          height: 20,
          width: 32,
          display: 'flex',
          alignContent: 'center',
          alignItems: 'center',
          background: theme.palette.error[300],
          '.MuiChip-label': {
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
      label: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        fontSize: 12,
      },
      outlined: {
        '&.MuiChip-colorDefault': {
          backgroundColor: theme.palette.grey[100],
          borderColor: theme.palette.grey[500],
        },
        '&.MuiChip-colorPrimary': {
          color: theme.palette.grey[800],
          backgroundColor: theme.palette.primary[200],
          borderColor: theme.palette.primary[500],
        },
        '&.MuiChip-colorSuccess': {
          color: theme.palette.grey[800],
          backgroundColor: theme.palette.success[300],
          borderColor: theme.palette.success[500],
        },
        '&.MuiChip-colorError': {
          color: theme.palette.grey[800],
          backgroundColor: theme.palette.error[200],
          borderColor: theme.palette.error[400],
        },
        '&.MuiChip-colorWarning': {
          color: theme.palette.grey[800],
          backgroundColor: theme.palette.warning[300],
          borderColor: theme.palette.warning.main,
        },
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '32px 32px 0',
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        marginTop: theme.spacing(0.5),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(4),
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '24px 0 0 0',
        gap: theme.spacing(3),
        '& > :not(:first-of-type)': {
          marginLeft: 0,
        },
        '&.MuiPickersLayout-actionBar': {
          padding: '16px',
        },
      },
    },
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: {
        color: theme.palette.grey[900],
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
    },
  },

  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        padding: '8px 12px',
        backgroundColor: theme.palette.grey[800],
        fontSize: '12px',
        lineHeight: '16px',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
      arrow: {
        '&::before': {
          backgroundColor: theme.palette.grey[800],
        },
      },
    },
  },

  MuiFormLabel: {
    styleOverrides: {
      root: {
        position: 'relative',
        transform: 'none',
        color: theme.palette.grey[800],
        ...theme.typography.body2,

        [`&.${formLabelClasses.focused}`]: {
          color: theme.palette.grey[800],
        },
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          backgroundColor: theme.palette.secondary[400],
        },
      },
    },
  },

  MuiBackdrop: {
    styleOverrides: {
      root: {
        zIndex: theme.zIndex.modal + 1,
        backgroundColor: alpha(theme.palette.grey.black, 0.5),
        '&.MuiBackdrop-invisible': {
          backgroundColor: 'transparent',
        },
      },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: {
        '&:hover': {
          fontWeight: 500,
        },
      },
    },
  },

  MuiListItemButton: {
    variants: [
      {
        props: { variant: 'sidebar' },
        style: {
          display: 'flex',
          gap: 8,
          paddingLeft: 14,
          '.MuiCollapse-root .MuiList-root &': {
            paddingTop: 4,
            paddingBottom: 4,
          },
          '&:hover': {
            backgroundColor: theme.palette.primary[200],
            '& .MuiListItemText-primary': {
              color: theme.palette.primary[500],
            },
            '& > .MuiSvgIcon-root': {
              color: theme.palette.primary[500],
            },
          },
          '&:hover:not(.Mui-selected)': {
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary[500],
            },
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary[200],
            '&:hover': {
              backgroundColor: theme.palette.secondary[500],
            },
            '& .MuiListItemIcon-root': {
              background: theme.palette.primary[500],
              color: theme.palette.grey.white,
            },
            '& .MuiListItemText-primary': {
              color: theme.palette.primary[500],
              fontWeight: 700,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              borderTopRightRadius: 999,
              borderBottomRightRadius: 999,
              backgroundColor: theme.palette.primary[500],
            },
          },
        },
      },
    ],
  },

  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minHeight: 32,
        minWidth: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        color: theme.palette.grey[700],
      },
    },
  },

  MuiListItemText: {
    styleOverrides: {
      root: {},
      primary: {
        fontSize: 14,
        color: theme.palette.grey.black,
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: 57,
        '@media (min-width: 600px)': {
          minHeight: 57,
        },
      },
    },
  },

  ...applicationBasedStyles,
});

export default getOverriddenStyles;
